import React, { createContext, useContext, useMemo, useState } from 'react';

interface NavMode {
  /** True while a full-bleed photograph sits under the fixed nav. */
  onPhoto: boolean;
  setOnPhoto: (value: boolean) => void;
  /**
   * True while a landing hero is mounted. The nav then reads `--hero-p` (0 on
   * arrival, 1 once the visitor has scrolled half a viewport) and travels from
   * the middle of the photograph to the top of the page.
   */
  landing: boolean;
  setLanding: (value: boolean) => void;
  /**
   * True for a moment after a route change. The nav's position and the
   * wordmark's size are normally scroll-linked and move without transitions;
   * during a route change the page scrolls to the top and `--hero-p` jumps, so
   * for that moment the same properties ease instead of snapping.
   */
  transitioning: boolean;
  setTransitioning: (value: boolean) => void;
  /** True while the phone menu is open. Overlays (the waitlist popup) wait for it. */
  menuOpen: boolean;
  setMenuOpen: (value: boolean) => void;
}

const NavModeContext = createContext<NavMode>({
  onPhoto: false,
  setOnPhoto: () => undefined,
  landing: false,
  setLanding: () => undefined,
  transitioning: false,
  setTransitioning: () => undefined,
  menuOpen: false,
  setMenuOpen: () => undefined,
});

export function NavModeProvider({ children }: { children: React.ReactNode }) {
  const [onPhoto, setOnPhoto] = useState(false);
  const [landing, setLanding] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const value = useMemo(
    () => ({ onPhoto, setOnPhoto, landing, setLanding, transitioning, setTransitioning, menuOpen, setMenuOpen }),
    [onPhoto, landing, transitioning, menuOpen],
  );
  return <NavModeContext.Provider value={value}>{children}</NavModeContext.Provider>;
}

export const useNavMode = () => useContext(NavModeContext);
